import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type UpdateBuyerDealStructurePayload,
} from "@/api/buyers";
import { buyerSubmissionKeys } from "./buyerKeys";

/**
 * The Potential Deal Structure — read, and saved a step at a time.
 *
 * <p>🔴 <b>No model call anywhere.</b> The read is deterministic (the Suggested
 * structure comes from the listing and the buyer's prequalification or
 * assessment), and the PATCH saves only what staff confirmed. The server is the
 * only calculator: every figure the surface shows is read from the response.</p>
 */
export function useBuyerDealStructure(submissionId: string, enabled = true) {
  return useQuery({
    queryKey: buyerSubmissionKeys.dealStructure(submissionId),
    queryFn: () => buyerSubmissionsApi.getDealStructure(submissionId),
    enabled: enabled && Boolean(submissionId),
  });
}

/**
 * Save one wizard step, or the EDIT modal's whole structure.
 *
 * <p>The response is the refreshed structure, so it is written straight into
 * its key (the wizard's Review step reads the figures the server just computed
 * without waiting for a refetch), then the whole submission prefix is
 * invalidated: a save moves the detail read's step and completion, document
 * 3's blocked reason on the package, and the lists' "Deal structure price".</p>
 *
 * <p>A refused figure is a 400 whose message is the sentence to show; the
 * caller renders it and does not advance.</p>
 */
export function usePatchBuyerDealStructure(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBuyerDealStructurePayload) =>
      buyerSubmissionsApi.patchDealStructure(submissionId, payload),
    onSuccess: (dto) => {
      qc.setQueryData(buyerSubmissionKeys.dealStructure(submissionId), dto);
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
    },
  });
}
